import React from 'react';
import { Box, Typography, Avatar, Tooltip } from '@mui/material';
import { keyframes } from '@emotion/react';
import {
  SupportAgent, // Agent
  HomeWork, // Vendor
  Groups, // Visitor
  MonetizationOn, // Buyer
  Gavel, // Auctioneer
  Description, // Conveyancer
  Handyman, // Handyman
  Weekend, // Stylist
  CameraAlt, // Photographer
  Signpost, // Sign Board
  Public, // Portals
  Hub // Central Hub
} from '@mui/icons-material';

// Animations
const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4); }
  70% { transform: scale(1.1); box-shadow: 0 0 0 15px rgba(25, 118, 210, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

const flowIn = keyframes`
  0% { stroke-dashoffset: 100; opacity: 0; }
  20% { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 1; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

interface NodeData {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const nodes: NodeData[] = [
  { id: 'agent', label: 'Agent', icon: <SupportAgent />, color: '#1976d2' }, // Primary Blue
  { id: 'vendor', label: 'Vendor', icon: <HomeWork />, color: '#2e7d32' }, // Green
  { id: 'buyer', label: 'Buyer', icon: <MonetizationOn />, color: '#ed6c02' }, // Orange
  { id: 'visitor', label: 'Visitors', icon: <Groups />, color: '#9c27b0' }, // Purple
  { id: 'auctioneer', label: 'Auctioneer', icon: <Gavel />, color: '#d32f2f' }, // Red
  { id: 'conveyancer', label: 'Conveyancer', icon: <Description />, color: '#0288d1' }, // Light Blue
  { id: 'portals', label: 'Portals', icon: <Public />, color: '#7b1fa2' }, // Deep Purple
  { id: 'signboard', label: 'Sign Board', icon: <Signpost />, color: '#ef6c00' }, // Dark Orange
  { id: 'photographer', label: 'Photographer', icon: <CameraAlt />, color: '#00796b' }, // Teal
  { id: 'stylist', label: 'Stylist', icon: <Weekend />, color: '#c2185b' }, // Pink
  { id: 'handyman', label: 'Handyman', icon: <Handyman />, color: '#455a64' }, // Blue Grey
];

export const DashboardAnimation = () => {
  const containerSize = 600;
  const radius = 220; // Radius of the circle of stakeholders
  const center = containerSize / 2;

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        width: '100%', 
        maxWidth: containerSize, 
        aspectRatio: '1/1',
        height: 'auto',
        maxHeight: 600,
        bgcolor: 'background.paper',
        borderRadius: 8,
        boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        margin: '0 auto'
      }}
    >
      {/* Connecting Lines (SVG Layer) */}
      <svg 
        width="100%" 
        height="100%" 
        viewBox={`0 0 ${containerSize} ${containerSize}`} 
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      >
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#e0e0e0" />
            <stop offset="100%" stopColor="#1976d2" />
          </linearGradient>
        </defs>
        {nodes.map((node, index) => {
          const angle = (index * 360) / nodes.length - 90; // Start from top (-90 deg)
          const angleRad = (angle * Math.PI) / 180;
          const x = center + radius * Math.cos(angleRad);
          const y = center + radius * Math.sin(angleRad);
          
          return (
            <g key={`line-${node.id}`}>
              {/* Static faint line */}
              <line 
                x1={center} 
                y1={center} 
                x2={x} 
                y2={y} 
                stroke="#f0f0f0" 
                strokeWidth="2" 
              />
              {/* Animated flowing particle/line */}
              <line 
                x1={x} 
                y1={y} 
                x2={center} 
                y2={center} 
                stroke={node.color} 
                strokeWidth="2"
                strokeDasharray="10 200"
                strokeLinecap="round"
                opacity="0.6"
              >
                <animate 
                  attributeName="stroke-dashoffset" 
                  from="210" 
                  to="0" 
                  dur={`${3 + (index % 3) * 0.5}s`} 
                  repeatCount="indefinite" 
                />
              </line>
            </g>
          );
        })}
      </svg>

      {/* Central Hub */}
      <Box 
        sx={{ 
          position: 'absolute', 
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          animation: `${fadeIn} 1s ease-out`
        }}
      >
        <Avatar 
          sx={{ 
            width: 80, 
            height: 80, 
            bgcolor: 'primary.main',
            boxShadow: '0 0 20px rgba(25, 118, 210, 0.3)',
            mb: 1,
            animation: `${pulse} 3s infinite ease-in-out`
          }}
        >
          <Hub sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary', bgcolor: 'background.paper', px: 1, borderRadius: 1 }}>
          Workflow.ESTATE
        </Typography>
      </Box>

      {/* Surrounding Nodes */}
      {nodes.map((node, index) => {
        const angle = (index * 360) / nodes.length - 90;
        const angleRad = (angle * Math.PI) / 180;
        // Position relative to center of container, adjusting for node size (roughly 50px)
        const x = center + radius * Math.cos(angleRad);
        const y = center + radius * Math.sin(angleRad);
        
        // Convert to percentage for responsiveness if needed, but fixed px is easier with SVG match
        // We'll use absolute positioning with margins
        
        return (
          <Box
            key={node.id}
            sx={{
              position: 'absolute',
              left: x,
              top: y,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              animation: `${fadeIn} 0.5s ease-out ${index * 0.1}s backwards, ${float} ${3 + Math.random()}s ease-in-out infinite alternate`,
              zIndex: 5,
              cursor: 'default'
            }}
          >
            <Tooltip title={node.label} placement="top">
              <Avatar 
                sx={{ 
                  width: 50, 
                  height: 50, 
                  bgcolor: 'white', 
                  color: node.color,
                  border: `2px solid ${node.color}`,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                  mb: 0.5,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.15)',
                    bgcolor: node.color,
                    color: 'white',
                    zIndex: 20
                  }
                }}
              >
                {node.icon}
              </Avatar>
            </Tooltip>
            <Typography 
              variant="caption" 
              align="center"
              sx={{ 
                fontWeight: 600, 
                color: 'text.secondary',
                bgcolor: 'rgba(255,255,255,0.9)',
                px: 0.5,
                borderRadius: 1,
                fontSize: '0.7rem',
                whiteSpace: 'nowrap',
                maxWidth: 100,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {node.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default DashboardAnimation;
